import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Edit, Trash2, X } from 'lucide-react';

const ProjectFormModal = ({ project, onClose, onSave }) => {
    const [formData, setFormData] = useState(project || { name: '', website: '', email: '', phone: '', description: '' });
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name) {
            setError('Project name is required.');
            return;
        }
        setError('');
        await onSave(formData);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[400] flex items-center justify-center p-6 bg-black/90 backdrop-blur-xl"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#111] border border-white/10 p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
                onClick={e => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-[#00f3ff]"><X size={24} /></button>
                <h3 className="text-2xl font-black uppercase tracking-tighter mb-8 text-[#00f3ff]">{project ? 'Edit Project' : 'Add New Project'}</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="name" value={formData.name} onChange={handleChange} placeholder="Project Name*" className="w-full bg-zinc-900/50 p-3 border-b border-white/10 focus:border-[#00f3ff] outline-none" />
                    <input name="website" value={formData.website} onChange={handleChange} placeholder="Website URL" className="w-full bg-zinc-900/50 p-3 border-b border-white/10 focus:border-[#00f3ff] outline-none" />
                    <input name="email" value={formData.email} onChange={handleChange} placeholder="Contact Email" className="w-full bg-zinc-900/50 p-3 border-b border-white/10 focus:border-[#00f3ff] outline-none" />
                    <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Contact Phone" className="w-full bg-zinc-900/50 p-3 border-b border-white/10 focus:border-[#00f3ff] outline-none" />
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" rows="5" className="w-full bg-zinc-900/50 p-3 border-b border-white/10 focus:border-[#00f3ff] outline-none" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-6 py-2 border border-white/20 uppercase text-xs tracking-widest hover:bg-zinc-800">Cancel</button>
                        <button type="submit" className="px-6 py-2 bg-white text-black uppercase text-xs tracking-widest font-bold hover:bg-[#00f3ff]">Save Project</button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

const AdminDashboard = () => {
    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);

    const fetchProjects = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/projects.php`, { credentials: 'include' });
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            setProjects(data);
        } catch (err) {
            setError('Could not load projects.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleLogout = async () => {
        await fetch(`${process.env.REACT_APP_API_URL}/logout.php`, { method: 'POST', credentials: 'include' });
        setIsAuthenticated(false);
        navigate('/');
    };

    const handleSaveProject = async (projectData) => {
        const isUpdating = !!projectData.id;
        const url = `${process.env.REACT_APP_API_URL}/projects.php`;
        const method = isUpdating ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectData),
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Save failed');
            await fetchProjects(); // Re-fetch to update list
            setIsModalOpen(false);
            setEditingProject(null);
        } catch (err) {
            console.error("Save error:", err);
        }
    };

    const handleDeleteProject = async (projectId) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/projects.php?id=${projectId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (!response.ok) throw new Error('Delete failed');
                await fetchProjects();
            } catch (err) {
                console.error("Delete error:", err);
            }
        }
    };

    const openAddModal = () => {
        setEditingProject(null);
        setIsModalOpen(true);
    };

    const openEditModal = (project) => {
        setEditingProject(project);
        setIsModalOpen(true);
    };

    if (isLoading) return <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">Loading Admin...</div>;
    if (error) return <div className="min-h-screen bg-[#0a0a0a] text-red-500 flex items-center justify-center">{error}</div>;

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] p-6 md:p-12 font-sans">
            <header className="flex justify-between items-center mb-12">
                <h1 className="text-4xl font-black uppercase tracking-tighter">Admin Dashboard</h1>
                <div>
                    <button onClick={openAddModal} className="mr-4 px-4 py-2 bg-white text-black font-bold uppercase text-xs tracking-widest hover:bg-[#00f3ff] transition-all inline-flex items-center gap-2">
                        <Plus size={16} /> Add Project
                    </button>
                    <button onClick={handleLogout} className="px-4 py-2 border border-white/20 uppercase text-xs tracking-widest hover:bg-zinc-800">
                        Logout
                    </button>
                </div>
            </header>

            <div className="overflow-x-auto bg-[#111] border border-white/10">
                <table className="w-full text-left text-sm">
                    <thead className="bg-zinc-900/50 text-xs uppercase tracking-widest">
                        <tr>
                            <th className="p-4">Name</th>
                            <th className="p-4">Website</th>
                            <th className="p-4 hidden md:table-cell">Email</th>
                            <th className="p-4 hidden lg:table-cell">Date Created</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.length > 0 ? (
                            projects.map(p => (
                                <tr key={p.id} className="border-t border-white/5 hover:bg-zinc-900 transition-colors">
                                    <td className="p-4 font-bold">{p.name}</td>
                                    <td className="p-4">
                                        <a href={p.website} target="_blank" rel="noopener noreferrer" className="hover:text-[#00f3ff] truncate block max-w-xs">
                                            {p.website}
                                        </a>
                                    </td>
                                    <td className="p-4 hidden md:table-cell">{p.email}</td>
                                    <td className="p-4 hidden lg:table-cell">{new Date(p.date_created).toLocaleDateString()}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => openEditModal(p)} className="p-2 text-zinc-400 hover:text-white">
                                            <Edit size={16} />
                                        </button>
                                        <button onClick={() => handleDeleteProject(p.id)} className="p-2 text-zinc-400 hover:text-red-500">
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center p-8 text-zinc-500">
                                    No projects found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <ProjectFormModal
                        project={editingProject}
                        onClose={() => setIsModalOpen(false)}
                        onSave={handleSaveProject}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminDashboard;